/**
 * Image Component Definition
 *
 * An image component with sizing, positioning, and styling options.
 */

export const imageComponent = {
  type: 'image',
  label: 'Image',
  icon: 'Image',
  description: 'Image with sizing and styling options',
  category: 'content',

  canHaveChildren: false,

  resolveStyles(props) {
    return {
      objectFit: props.objectFit || 'cover',
      objectPosition: props.objectPosition || 'center',
      display: 'block',
      maxWidth: '100%',
    };
  },

  defaultProps: {
    // Content
    src: 'https://placehold.co/600x400?text=New+Image',
    alt: 'Image',
    linkUrl: '',
    openInNewTab: false,

    // Size (responsive format)
    width: { base: { value: 100, unit: '%' }, tablet: null, desktop: null },
    height: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    minWidth: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    minHeight: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    maxWidth: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    maxHeight: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    objectFit: 'cover',
    objectPosition: 'center',
    aspectRatio: 'auto',

    // Spacing
    padding: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },
    margin: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },

    // Border
    borderWidth: 0,
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderRadius: 0,

    // Effects
    opacity: 100,
    filter: 'none',

    // Advanced
    visibility: { base: true, tablet: null, desktop: null },

    // Positioning
    position: 'static',
    top: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    right: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    bottom: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    left: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    zIndex: 'auto',

    // Custom CSS
    customCSS: '',

    // Flex child (when inside a flex parent) — responsive
    flexChild: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
  },

  // Quick settings - shown at top of properties panel
  quickSettings: [
    {
      key: 'objectFit',
      type: 'iconToggle',
      label: 'Fit',
      options: [
        { value: 'cover', icon: 'Maximize2', label: 'Cover' },
        { value: 'contain', icon: 'Minimize2', label: 'Contain' },
        { value: 'fill', icon: 'Square', label: 'Fill' },
        { value: 'none', icon: 'Image', label: 'None' },
      ],
    },
    {
      key: 'src',
      type: 'mediaPicker',
      label: 'Source',
      acceptTypes: 'image',
      placeholder: 'https://...',
    },
    {
      key: 'alt',
      type: 'text',
      label: 'Alt',
      placeholder: 'Image description',
    },
  ],

  settingsSchema: [
    // Content category
    {
      key: 'src',
      type: 'mediaPicker',
      label: 'Image',
      category: 'Content',
      acceptTypes: 'image',
      placeholder: 'https://...',
    },
    {
      key: 'alt',
      type: 'text',
      label: 'Alt Text',
      category: 'Content',
      default: 'Image',
      description: 'Accessibility description',
    },
    {
      key: 'linkUrl',
      type: 'link',
      label: 'Link URL',
      category: 'Content',
      placeholder: 'https://...',
    },
    {
      key: 'openInNewTab',
      type: 'checkbox',
      label: 'Open link in new tab',
      category: 'Content',
      default: false,
      showWhen: { field: 'linkUrl', notEquals: '' },
    },

    // Size category
    {
      key: 'width',
      type: 'responsiveDimension',
      label: 'Width',
      category: 'Size',
    },
    {
      key: 'height',
      type: 'responsiveDimension',
      label: 'Height',
      category: 'Size',
    },
    {
      key: 'minWidth',
      type: 'responsiveDimension',
      label: 'Min Width',
      category: 'Size',
    },
    {
      key: 'minHeight',
      type: 'responsiveDimension',
      label: 'Min Height',
      category: 'Size',
    },
    {
      key: 'maxWidth',
      type: 'responsiveDimension',
      label: 'Max Width',
      category: 'Size',
    },
    {
      key: 'maxHeight',
      type: 'responsiveDimension',
      label: 'Max Height',
      category: 'Size',
    },
    {
      key: 'objectFit',
      type: 'select',
      label: 'Object Fit',
      category: 'Size',
      default: 'cover',
      options: [
        { value: 'cover', label: 'Cover' },
        { value: 'contain', label: 'Contain' },
        { value: 'fill', label: 'Fill' },
        { value: 'none', label: 'None' },
        { value: 'scale-down', label: 'Scale Down' },
      ],
    },
    {
      key: 'objectPosition',
      type: 'select',
      label: 'Object Position',
      category: 'Size',
      default: 'center',
      options: [
        { value: 'center', label: 'Center' },
        { value: 'top', label: 'Top' },
        { value: 'bottom', label: 'Bottom' },
        { value: 'left', label: 'Left' },
        { value: 'right', label: 'Right' },
        { value: 'top left', label: 'Top Left' },
        { value: 'top right', label: 'Top Right' },
        { value: 'bottom left', label: 'Bottom Left' },
        { value: 'bottom right', label: 'Bottom Right' },
      ],
    },
    {
      key: 'aspectRatio',
      type: 'select',
      label: 'Aspect Ratio',
      category: 'Size',
      default: 'auto',
      options: [
        { value: 'auto', label: 'Auto' },
        { value: '1/1', label: '1:1 (Square)' },
        { value: '4/3', label: '4:3' },
        { value: '16/9', label: '16:9' },
        { value: '21/9', label: '21:9 (Cinematic)' },
        { value: '3/4', label: '3:4 (Portrait)' },
        { value: '9/16', label: '9:16 (Mobile)' },
      ],
    },

    // Spacing category
    {
      key: 'padding',
      type: 'responsivePadding',
      label: 'Padding',
      category: 'Spacing',
    },
    {
      key: 'margin',
      type: 'responsiveMargin',
      label: 'Margin',
      category: 'Spacing',
    },

    // Border category
    {
      key: 'borderWidth',
      type: 'number',
      label: 'Border Width (px)',
      category: 'Border',
      min: 0,
      max: 20,
      default: 0,
    },
    {
      key: 'borderStyle',
      type: 'select',
      label: 'Border Style',
      category: 'Border',
      default: 'solid',
      options: [
        { value: 'none', label: 'None' },
        { value: 'solid', label: 'Solid' },
        { value: 'dashed', label: 'Dashed' },
        { value: 'dotted', label: 'Dotted' },
      ],
    },
    {
      key: 'borderColor',
      type: 'color',
      label: 'Border Color',
      category: 'Border',
      default: 'transparent',
    },
    {
      key: 'borderRadius',
      type: 'number',
      label: 'Border Radius (px)',
      category: 'Border',
      min: 0,
      max: 100,
      default: 0,
    },

    // Effects category
    {
      key: 'opacity',
      type: 'number',
      label: 'Opacity (%)',
      category: 'Effects',
      min: 0,
      max: 100,
      default: 100,
    },
    {
      key: 'filter',
      type: 'select',
      label: 'Filter',
      category: 'Effects',
      default: 'none',
      options: [
        { value: 'none', label: 'None' },
        { value: 'grayscale(100%)', label: 'Grayscale' },
        { value: 'sepia(100%)', label: 'Sepia' },
        { value: 'blur(2px)', label: 'Blur (Light)' },
        { value: 'blur(5px)', label: 'Blur (Heavy)' },
        { value: 'brightness(120%)', label: 'Bright' },
        { value: 'brightness(80%)', label: 'Dim' },
        { value: 'contrast(120%)', label: 'High Contrast' },
        { value: 'saturate(150%)', label: 'Saturated' },
      ],
    },

    // Advanced category
    {
      key: 'visibility',
      type: 'responsiveVisibility',
      label: 'Visibility',
      category: 'Advanced',
      default: { base: true, tablet: null, desktop: null },
    },

    // Position category
    {
      key: 'position',
      type: 'select',
      label: 'Position',
      category: 'Position',
      default: 'static',
      options: [
        { value: 'static', label: 'Static (Normal)' },
        { value: 'relative', label: 'Relative' },
        { value: 'absolute', label: 'Absolute' },
        { value: 'fixed', label: 'Fixed' },
        { value: 'sticky', label: 'Sticky' },
      ],
    },
    {
      key: 'top',
      type: 'responsiveDimension',
      label: 'Top',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'right',
      type: 'responsiveDimension',
      label: 'Right',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'bottom',
      type: 'responsiveDimension',
      label: 'Bottom',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'left',
      type: 'responsiveDimension',
      label: 'Left',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'zIndex',
      type: 'number',
      label: 'Z-Index',
      category: 'Position',
      placeholder: 'auto',
      showWhen: { field: 'position', notEquals: 'static' },
    },

    {
      key: 'customCSS',
      type: 'customCSS',
      label: 'Custom CSS',
      category: 'Advanced',
      default: '',
    },

    // Flex Child sizing
    {
      key: 'flexChild',
      type: 'flexChild',
      label: 'Flex Child',
      category: 'Layout',
      showWhenParent: { field: 'display', in: ['flex', 'inline-flex'] },
      description: 'Controls sizing when inside a flex container',
      default: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
    },
  ],

  // Render function - will be implemented in the unified renderer
  render: null,
};

export default imageComponent;
